import { _, $, Backbone } from "js/vendor";
import { AnimationPresetType, BuildAnimationType, BuildAnimationSpeed, AnimationSequenceType, AnimationStepType } from "common/constants";
import log from "js/core/logger";

class AnimationManager {
    get buildAnimationType() {
        return this._buildAnimationType;
    }

    set buildAnimationType(value) {
        this._buildAnimationType = value;
        if (window.isPlayer) {
            return;
        }
        this.saveModel();
    }

    get speed() {
        return this._speed;
    }

    set speed(value) {
        this._speed = value;
        this.saveModel();
    }

    get animationDuration() {
        switch (this.speed) {
            case BuildAnimationSpeed.SLOW:
                return 1200;
            case BuildAnimationSpeed.FAST:
                return 300;
            case BuildAnimationSpeed.NORMAL:
            default:
                return 600;
        }
    }

    get stagger() {
        return this.animationDuration / 3;
    }

    get preset() {
        return this._preset;
    }

    set preset(type) {
        this._preset = type;
        this.loadPreset(type);
        this.saveModel();
    }

    get showAnimationProperties() {
        return this._showAnimationProperties && this.steps.length;
    }
    set showAnimationProperties(value) {
        this._showAnimationProperties = value;
    }

    constructor(canvas) {
        _.extend(this, Backbone.Events);

        this._showAnimationProperties = true;
        this.canvas = canvas;
        this.steps = [];

        let model = this.canvas.slide.get("timeline");
        model = _.defaults({}, model, {
            buildAnimationType: BuildAnimationType.OVERLAPPING,
            preset: AnimationPresetType.AUTOPLAY,
            speed: BuildAnimationSpeed.NORMAL,
            timeline: []
        });

        if (model.preset == AnimationPresetType.CUSTOM) {
            log.debug(log.ANIMATION)("Load Model", model);
            this.loadModel(model);
            this.linkToCanvas();
        } else {
            log.debug(log.ANIMATION)("Load Preset", model.preset);
            this.loadPreset(model.preset);
        }

        this.currentStep = -1;
    }

    loadModel(model) {
        this._speed = model.speed;
        this._buildAnimationType = model.buildAnimationType;
        this._preset = model.preset;

        this.steps = [];
        for (let step of model.timeline.filter(timelineItem => timelineItem)) {
            switch (step.type) {
                case AnimationStepType.PLAY_ANIMATION:
                    this.steps.push(new AnimationSequence(step.id, this.canvas.layouter.requestAnimationFrame, this.canvas));
                    break;
                case AnimationStepType.WAIT_FOR_CLICK:
                    this.steps.push(new AnimationWaitForClick(step.id));
                    break;
                case AnimationStepType.DELAY:
                    this.steps.push(new AnimationDelay(step.id, step.delay));
                    break;
                case AnimationStepType.CHANGE_DATA:
                    this.steps.push(new AnimationChangeData(step.id));
                    break;
            }
        }

        log.debug(log.ANIMATION)("loaded model", this.buildAnimationType, this.preset, this.steps);
    }

    saveModel() {
        let model = {
            preset: this.preset,
            speed: this.speed,
            buildAnimationType: this.buildAnimationType,
            timeline: this.steps.map(step => step.getData())
        };

        log.debug(log.ANIMATION)("Save model", model);
        this.canvas.slide.update({ timeline: model });
    }

    linkToCanvas() {
        this.canvas.registerAnimations(this);

        // clean up any steps that were not linked to from within registerAnimations (ie. steps that are orphaned because the element they referred to have been removed)
        for (let i = this.steps.length - 1; i >= 0; i--) {
            let step = this.steps[i];
            if (step.type == AnimationStepType.PLAY_ANIMATION && !step.linked) {
                this.steps.remove(step);
            }
        }
    }

    getStep(id) {
        return this.steps.find(i => i.id == id);
    }

    findOrCreateAnimationSequence(id, label) {
        let step = this.getStep(id);
        if (!step) {
            step = new AnimationSequence(id, this.canvas.layouter.requestAnimationFrame, this.canvas);
            this.steps.push(step);
        }
        step.label = label;

        step.linked = true;
        return step;
    }

    addWaitForClickStep(index) {
        let waitForClick = new AnimationWaitForClick(_.uniqueId());
        this.steps.insert(waitForClick, index);

        this.saveModel();
        return waitForClick;
    }

    addDelayStep(index, time) {
        let delay = new AnimationDelay(_.uniqueId(), time);
        this.steps.insert(delay, index);
        this.saveModel();
        return delay;
    }

    removeStep(step) {
        this.steps.remove(step);
        this.saveModel();
    }

    reorderStep(id, newIndex) {
        let step = this.getStep(id);
        if (step) {
            this.steps.move(this.steps.indexOf(step), newIndex);
        }
        this.saveModel();
    }

    prepare() {
        this.isPrepared = true;

        for (let step of this.steps.filter(step => !step.isDOMControlled)) {
            step.prepare();
            if (this.buildAnimationType === BuildAnimationType.NONE) {
                step.reset();
            }
        }

        this.canvas.isAnimating = true;
        return this.canvas.refreshCanvas().then(() => {
            for (let step of this.steps.filter(step => step.isDOMControlled)) {
                step.prepare();
                if (this.buildAnimationType === BuildAnimationType.NONE) {
                    step.reset();
                }
            }
        });
    }

    start() {
        this.hasStarted = true;

        // dedupe any repeated WAIT/DELAY steps
        for (let i = this.steps.length - 1; i >= 1; i--) {
            let thisStep = this.steps[i];
            let prevStep = this.steps[i - 1];
            if ((thisStep.type == AnimationStepType.WAIT_FOR_CLICK || thisStep.type == AnimationStepType.DELAY) && (prevStep.type == AnimationStepType.WAIT_FOR_CLICK || prevStep.type == AnimationStepType.DELAY)) {
                this.steps.remove(thisStep);
                log.debug(log.ANIMATION)("remove repeated wait/delay step");
            }
        }
        log.debug(log.ANIMATION)("start animation");

        if (this.steps.length) {
            this.executeNextStep(1);
        } else {
            this.trigger("complete");
            log.debug(log.ANIMATION)("Animation Timeline complete (no steps)");
        }
    }

    cancel() {
        log.debug(log.ANIMATION)("Cancel animation");
        this.hasStarted = false;
        if (this.steps) {
            for (let step of this.steps) {
                if (step.isPlaying) {
                    step.cancel();
                }
            }
        }
        this.currentStep = -1;

        this.canvas.isAnimating = false;

        // MJG - i'm disabling this because when you playerClose() is called, it calls stopCurrentSlide() which calls this function
        // but because refreshCanvas is async, the canvas is removed before refreshCanvas is complete
        // I'm not sure if we need to refresh canvas on cancel...
        // this.canvas.refreshCanvas();
    }

    reset() {
        log.debug(log.ANIMATION)("Reset animation");
        this.hasStarted = false;
        if (this.steps) {
            for (let step of this.steps) {
                step.reset();
            }
        }

        this.canvas.isAnimating = false;
        this.canvas.refreshCanvas();
    }

    get hasAnimation() {
        return this.steps.length != 0;
    }

    get isPlaying() {
        return this.hasStarted && this.currentStep < this.steps.length && this.steps.length > 0;
    }

    get isPlayingLastStep() {
        return this.currentStep == this.steps.length - 1;
    }

    onComplete() {
        // Explicitly enforcing canvar re-rendering without isAnimating
        this.canvas.isAnimating = false;
        this.canvas.refreshCanvas();

        this.isComplete = true;
        this.trigger("complete");
        log.debug(log.ANIMATION)("Animation Timeline complete");
    }

    executeNextStep() {
        let nextStep = this.currentStep + 1;
        if (nextStep >= this.steps.length) {
            this.onComplete();
            return;
        }

        this.executeStep(nextStep, false);
    }

    executeStep(stepIndex, forcePlay) {
        // if (stepIndex <= this.currentStep || stepIndex > this.steps.length) return;
        log.debug(log.ANIMATION)("execute step", stepIndex);

        this.currentStep = stepIndex;

        let step = this.steps[this.currentStep];

        this.trigger("step", { index: this.currentStep, step: step });
        $(".player_frame, .preview-animation-shield").removeClass("wait-for-click");

        switch (step.type) {
            case AnimationStepType.PLAY_ANIMATION:
                switch (this.buildAnimationType) {
                    case BuildAnimationType.SEQUENTIAL:
                        // execute each step sequentially
                        log.debug(log.ANIMATION)("play sequential", step);
                        step.execute(this, 0).then(() => this.executeNextStep());
                        break;
                    case BuildAnimationType.NONE:
                        log.debug(log.ANIMATION)("play none", step);
                        step.reset(); // reset any step prepare instead of executing the step
                        this.executeNextStep();
                        break;
                    case BuildAnimationType.OVERLAPPING:
                    case BuildAnimationType.SIMULTANEOUS:
                        log.debug(log.ANIMATION)("play " + this.buildAnimationType);

                        let durationMultipler = step.durationMultiplier || 1;

                        // simultaneous and overlapping can play multiple steps at the same time - either starting at the same time or staggered

                        let stagger = this.buildAnimationType == BuildAnimationType.OVERLAPPING ? this.stagger * durationMultipler : 0;

                        // find all the steps until the next wait for click or delay
                        let staggerSteps = _.takeWhile(_.drop(this.steps, this.currentStep), (step, index) => {
                            return step.type != AnimationStepType.WAIT_FOR_CLICK && step.type != AnimationStepType.DELAY;
                        });

                        let delay = 0;
                        let promises = [];

                        // execute all the animation steps with delay
                        for (let staggerStep of staggerSteps) {
                            log.debug(log.ANIMATION)("play stagger step", staggerStep);
                            promises.push(staggerStep.execute(this, delay));
                            delay += stagger;
                            this.currentStep++;
                        }
                        this.currentStep--;

                        Promise.all(promises).then(() => this.executeNextStep());
                        break;
                }
                break;

            case AnimationStepType.WAIT_FOR_CLICK:
                if (!this.ignoreClicks) {
                    // wait for the player to call executeNextStep() from user interaction -  so we don't do anything after execute
                    log.debug(log.ANIMATION)("waiting for click");
                    step.execute();
                } else {
                    return;
                }
                break;

            case AnimationStepType.DELAY:
                // let the step execute the delay timeoue and call executeNextStep() when it's done
                log.debug(log.ANIMATION)("delay", step.delay);
                step.execute().then(() => this.executeNextStep(1));
                break;

            case AnimationStepType.CHANGE_DATA:
                step.execute().then(() => this.executeNextStep(1));
                break;
        }
    }

    goToStep(step) {
        if (this.currentStep == step || step >= this.steps.length || step < 0) return;

        this.ignoreClicks = true;
        // this.prepare();
        for (let current = this.currentStep + 1; current <= step; current++) {
            this.executeStep(current);
        }
    }

    loadPreset(type) {
        if (type == AnimationPresetType.CUSTOM) return;

        this.loadModel({
            preset: type,
            buildAnimationType: BuildAnimationType.OVERLAPPING,
            speed: BuildAnimationSpeed.NORMAL,
            stagger: 333,
            timeline: []
        });
        this.linkToCanvas();

        switch (type) {
            case AnimationPresetType.AUTOPLAY:
                // use default
                break;
            case AnimationPresetType.WAIT_FOR_CLICK:
                let count = this.steps.length;
                let index = 0;
                for (let i = 0; i < count; i++) {
                    let waitForClick = new AnimationWaitForClick(_.uniqueId());
                    this.steps.insert(waitForClick, index);
                    index += 2;
                }
                break;
            case AnimationPresetType.NO_ANIMATION:
                this._buildAnimationType = BuildAnimationType.NONE;
                break;
        }
    }

    setDefaultBuildAnimationType(value) {
        if (this.preset == AnimationPresetType.AUTOPLAY) {
            this.buildAnimationType = value;
        }
    }
}

class AnimationStep {
    constructor(id) {
        this.id = id;
    }

    prepare() {
    }

    reset() {
    }

    execute() {
        return Promise.resolve();
    }

    cancel() {

    }

    getData() {
        return {
            id: this.id,
            type: this.type
        };
    }
}

class AnimationSequence extends AnimationStep {
    get type() {
        return AnimationStepType.PLAY_ANIMATION;
    }

    get durationMultiplier() {
        return this._durationMultiplier || 1;
    }

    set durationMultiplier(value) {
        this._durationMultiplier = value;
    }

    constructor(id, requestAnimationFrame, canvas) {
        super(id);

        this.$canvas = canvas.$el;
        this.requestAnimationFrame = requestAnimationFrame;
        this.animations = [];
    }

    addAnimation(animation) {
        this.animations.push(animation);
    }

    prepare() {
        this.isPrepared = true;

        for (const animation of this.animations) {
            // Hide all animated elements and call custom prepare functions (if exist)
            animation.target && this.$canvas.find(animation.target).opacity(0);
            animation.prepare && animation.prepare();
        }
    }

    reset() {
        for (const animation of this.animations) {
            // Show all animated elements and call custom reset functions (if exist)
            animation.target && this.$canvas.find(animation.target).opacity(1);
            animation.reset && animation.reset();
        }
    }

    execute(animationManager, delay) {
        this.isCanceled = false;
        this.isPlaying = true;
        // Playing all animations (of the element) simultaneously
        return Promise.all(this.animations.map(animation => this.playAnimation(animation, animationManager, delay)))
            .then(() => {
                this.isPlaying = false;
            })
            .catch(err => log.error(err));
    }

    cancel() {
        log.debug(log.ANIMATION)("Cancel animation step");
        this.isCanceled = true;
        this.isPlaying = false;

        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        for (let animation of this.animations) {
            switch (animation.type) {
                case AnimationSequenceType.FADE:
                    this.$canvas.find(animation.target).velocity("stop").opacity(1);
                    break;
                case AnimationSequenceType.ANIMATE_TO_VALUE:
                    animation.callback(animation.endValue);
                    break;
            }
            animation.cancel && animation.cancel();  // call custom cancel callback
        }
    }

    playAnimation(animation, animationManager, overallDelay) {
        // console.log("play animation", animation.target.id(), animation);
        animation = _.defaults(animation, {
            type: AnimationSequenceType.FADE,
            duration: animationManager.animationDuration,
            delay: 0,
            startValue: 0,
            endValue: 1,
            easing: "swing"
        });

        return new Promise((resolve, reject) => {
            try {
                const duration = typeof animation.duration == "function" ? animation.duration(animationManager.animationDuration) : animation.duration * this.durationMultiplier;
                const delay = overallDelay + (typeof animation.delay == "function" ? animation.delay(duration) : animation.delay);

                switch (animation.type) {
                    case AnimationSequenceType.FADE:
                        animationManager.canvas.$el.find(animation.target).velocity({
                            opacity: [1, 0],
                        }, {
                            easing: animation.easing,
                            duration: duration,
                            delay: delay,
                            complete: () => {
                                resolve();
                            }
                        });
                        break;
                    case AnimationSequenceType.AUTOPLAY:
                        if (animation.play) {
                            this.timeout = setTimeout(() => {
                                animation.play(duration, animationManager.buildAnimationType).then(() => {
                                    resolve();
                                });
                            }, delay);
                        }
                        break;
                    case AnimationSequenceType.ANIMATE_TO_VALUE:
                        const delta = animation.endValue - animation.startValue;
                        const easing = $.Velocity.Easings[animation.easing];

                        let startTime;
                        const doAnimationStep = timestamp => {
                            if (this.isCanceled) {
                                return;
                            }

                            if (!startTime) {
                                startTime = timestamp;
                            }

                            const percentComplete = easing(Math.min((timestamp - startTime) / duration, 1));
                            const calculatedValue = animation.startValue + delta * percentComplete;

                            animation.callback(calculatedValue);

                            if (percentComplete < 1) {
                                this.requestAnimationFrame(animation.target, doAnimationStep);
                            } else {
                                animation.end && animation.end();
                                resolve();
                            }
                        };

                        this.timeout = setTimeout(() => {
                            animation.start && animation.start();
                            this.requestAnimationFrame(animation.target, doAnimationStep);
                        }, delay);

                        break;
                    default:
                        $(animation.target).opacity(1);
                        break;
                }
            } catch (err) {
                reject(err);
            }
        });
    }
}

class AnimationWaitForClick extends AnimationStep {
    get type() {
        return AnimationStepType.WAIT_FOR_CLICK;
    }

    execute() {
        $(".player_frame,.preview-animation-shield").addClass("wait-for-click");
    }

    cancel() {
        log.debug(log.ANIMATION)("Cancel wait step");
        $(".player_frame,.preview-animation-shield").removeClass("wait-for-click");
    }
}

class AnimationDelay extends AnimationStep {
    get type() {
        return AnimationStepType.DELAY;
    }

    constructor(id, delay = 1) {
        super(id);
        this.delay = delay;
    }

    execute() {
        this.isPlaying = true;
        return new Promise(resolve => {
            this.timeout = setTimeout(() => {
                this.isPlaying = false;
                resolve();
            }, this.delay * 1000);
        });
    }

    cancel() {
        this.isPlaying = false;
        if (this.timeout) {
            log.debug(log.ANIMATION)("Cancel delay step");
            clearTimeout(this.timeout);
        }
    }

    getData() {
        return {
            id: this.id,
            type: this.type,
            delay: this.delay
        };
    }
}

class AnimationChangeData extends AnimationStep {
    get type() {
        return AnimationStepType.CHANGE_DATA;
    }
}

export { AnimationManager };

